import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, LockKeyhole } from "lucide-react";
import { useSystemCatalogue, money, phaseLabel } from "@/components/system-builder/useSystemCatalogue";
import { KitListingPhoto } from "@/components/system-builder/KitPresentation";
import { ProductImage } from "@/components/system-builder/SystemScene";
import "@/components/system-builder/kit-presentation.css";

/* Branded navy kit card photo when one exists for this pack, otherwise the composed equipment photo. */
const KitCardPhoto = ({ pack, battery, inverter, panel }) => {
  const [failed, setFailed] = React.useState(false);
  if (failed) {
    return (
      <KitListingPhoto pack={pack}>
        <ProductImage product={battery} className="kit-battery-image" />
        <ProductImage product={inverter} className="kit-inverter-image" />
        <img src={panel.image} className="kit-panel-image" alt={panel.name} />
      </KitListingPhoto>
    );
  }
  return (
    <img
      src={`/media/kit-cards/${pack.id}.png`}
      alt={`${pack.name} — supplied equipment`}
      className="w-full h-44 sm:h-48 object-contain"
      loading="lazy"
      onError={() => setFailed(true)}
      data-testid={`product-kit-card-photo-${pack.id}`}
    />
  );
};

/* Ready-made kits that actually use this battery, matched on each pack's battery field. */
export const ProductKits = ({ batteryId, productName }) => {
  const { catalogue, error, retry } = useSystemCatalogue();
  const packs = (catalogue?.packs || []).filter((p) => p.battery_id === batteryId);

  return (
    <div className="bg-ei-navy text-ei-white rounded-md p-6 sm:p-8 lg:p-10" data-testid="product-ready-kits">
      <span className="ei-eyebrow text-ei-gold">Ready-made kits</span>
      <h2 className="mt-3 text-lg font-bold">Kits built on the {productName}</h2>
      <p className="mt-3 text-sm leading-relaxed text-ei-white/75 max-w-xl">
        Fixed products and fixed quantities. Only the packs that use this battery are listed here.
      </p>
      {error && (
        <div role="alert" className="mt-5 text-sm" data-testid="product-kits-error">
          Kit information could not load. <button className="underline min-h-[44px]" onClick={retry} data-testid="product-kits-retry">Try again</button>
        </div>
      )}
      {catalogue && packs.length === 0 && (
        <p className="mt-5 text-sm text-ei-white/70" data-testid="product-kits-empty">No ready-made kit currently uses this battery. Ask us for a configured quote.</p>
      )}
      {catalogue && packs.length > 0 && (
        <div className="mt-7 space-y-5">
          {packs.map((pack) => {
            const battery = catalogue.products.find((p) => p.id === pack.battery_id);
            const inverter = catalogue.products.find((p) => p.id === pack.inverter_id);
            const panel = catalogue.products.find((p) => p.id === pack.panel_id);
            return (
              <Link
                key={pack.id}
                to={`/kits/${pack.id}`}
                className="group w-full flex flex-col sm:flex-row sm:items-center gap-6 sm:gap-8 bg-white/[.04] border border-white/10 rounded-xl p-5 sm:p-6 hover:bg-white/[.07] hover:border-ei-gold/50 transition-colors"
                data-testid={`product-kit-${pack.id}`}
              >
                <div className="sm:w-64 lg:w-80 flex-shrink-0">
                  <KitCardPhoto pack={pack} battery={battery} inverter={inverter} panel={panel} />
                </div>
                <div className="flex-1 min-w-0">
                  <span className="flex items-center gap-2 text-[10px] uppercase tracking-wide text-ei-gold">
                    <LockKeyhole size={12} /> Fixed pack · no substitutions
                  </span>
                  <h3 className="text-base font-semibold mt-2">{pack.name}</h3>
                  <p className="text-xs text-ei-white/65 mt-2">
                    {phaseLabel(pack.phase)} · {pack.battery_count} {battery.family === "powerbox" ? "batteries" : "modules"} · {pack.panel_count} panels
                  </p>
                </div>
                <div className="sm:text-right flex-shrink-0">
                  <p className="text-xl font-semibold" data-testid={`product-kit-price-${pack.id}`}>
                    {money(pack.published_price)} <span className="text-xs">ex GST</span>
                  </p>
                  <p className="text-[10px] text-ei-white/60 mt-1">{money(pack.published_price_inc_gst)} incl GST · equipment reference, no rebates</p>
                  <span className="flex sm:justify-end items-center gap-2 mt-4 text-xs font-bold text-ei-gold">
                    View fixed inclusions <ArrowRight size={15} />
                  </span>
                </div>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
};
